/**
 * StudioAutoDeployFrame — hidden iframe that drives Studio to deploy a starter
 * template without any user interaction.
 *
 * Flow:
 *   1. Mount a hidden iframe pointing at Studio.
 *   2. Connect a BuilderHost; wait for the iframe's `ready` event.
 *   3. host.init(...) with the store/funnel/step context + session token.
 *   4. host.load({ mode: 'create', sourceData }) — sourceData is a FunnelTemplate
 *      wrapped around the starter's document, so Studio bypasses its template
 *      picker and loads the design directly.
 *   5. After a short delay (Studio needs a tick to wire the template into its
 *      store before the save listener can read it), host.requestSave().
 *   6. Studio runs its existing handleEmbeddedSave: bundle → upload → POST to
 *      the public deploy API → emit a `save` event with the resulting IDs.
 *   7. The component calls `onComplete` with `{ pluginId, deploymentId,
 *      instanceId }` and tears itself down.
 *
 * Sequential usage from the dialog: render one frame at a time. On `onComplete`
 * the parent updates funnel state and re-renders with the next step's starter.
 */

import { useEffect, useRef, useState } from 'react';
import { BuilderHost } from '@tagadapay/builder-protocol/host';
import type { SaveMessage } from '@tagadapay/builder-protocol';

export interface AutoDeployContext {
    storeId: string;
    funnelId: string;
    stepId: string;
    stepType: string;
    sessionToken: string;
}

export interface AutoDeployStarter {
    /** Starter id from apps/studio-app/src/data/templates (e.g. 'checkout-page-v2') */
    starterId: string;
    /** Display name from the starter */
    starterName: string;
    /** Category of the starter, used to derive the page type */
    category: string;
    /** Optional preview image (data: URI or absolute URL) shown in the
     *  funnel-creation progress UI while this starter is being deployed. */
    thumbnail?: string;
    /** Full Studio document JSON */
    document: unknown;
}

export interface AutoDeployResult {
    pluginId: string;
    deploymentId: string;
    instanceId?: string;
    /** Path of the page Studio deployed. Without it the node config carries no
     * path and fails server validation (TAG-1946). */
    pagePath?: string;
}

interface StudioAutoDeployFrameProps {
    /** Studio app URL — typically derived from getNodeEditorConfig() */
    builderUrl: string;
    /** Context passed through to Studio for store-scoped API auth */
    context: AutoDeployContext;
    /** Starter to deploy in this iframe lifecycle */
    starter: AutoDeployStarter;
    /**
     * Store branding (primaryColor + logoUrl + storeName). When provided,
     * the starter's `color.primary` token, `logo-image` Media.Image node,
     * and any `store-name` Heading/Text node are patched before the doc is
     * sent to Studio so the bundler bakes the store's brand into the
     * deployed page (instead of shipping starter defaults).
     */
    storeBranding?: { primaryColor?: string; logoUrl?: string; storeName?: string };
    /** Called once Studio replies with the deployment ids */
    onComplete: (result: AutoDeployResult) => void;
    /** Called if the iframe times out or Studio reports an error */
    onError: (message: string) => void;
    /** Override the wait-before-requestSave delay (ms). Default 600. */
    requestSaveDelayMs?: number;
    /** Override the total timeout for one deploy (ms). Default 90s. */
    timeoutMs?: number;
}

/**
 * Map starter category → Studio page type. Mirrors the mapping in
 * `apps/studio-app/src/editor/Editor.tsx` so the synthetic FunnelTemplate we
 * send via `sourceData` matches what Studio expects.
 */
const CATEGORY_TO_PAGE_TYPE: Record<string, string> = {
    'checkout': 'checkout',
    'thank-you': 'thankyou',
    'sales': 'offer',
    'landing': 'landing',
    'advertorial': 'landing',
    'vsl': 'landing',
};

function pageTypeFromCategory(category: string): string {
    return CATEGORY_TO_PAGE_TYPE[category] || 'custom';
}

function defaultPagePath(pageType: string): string {
    // Match the path conventions the existing manual flow uses for each step type
    switch (pageType) {
        case 'checkout': return '/checkout';
        case 'thankyou': return '/thankyou';
        case 'offer': return '/offer';
        case 'landing': return '/';
        default: return '/page';
    }
}

function generatePageId(pageType: string): string {
    const short = Math.random().toString(36).substring(2, 8);
    return `${pageType}-${short}`;
}

/**
 * Lighten/darken a hex color by a percentage. Used to derive a hover variant
 * from the store's primary color when only the base is provided.
 */
function shadeHex(hex: string, percent: number): string {
    const sanitized = hex.replace('#', '');
    if (sanitized.length !== 6) return hex;
    const num = parseInt(sanitized, 16);
    const r = Math.max(0, Math.min(255, ((num >> 16) & 0xff) + Math.round(255 * percent / 100)));
    const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + Math.round(255 * percent / 100)));
    const b = Math.max(0, Math.min(255, (num & 0xff) + Math.round(255 * percent / 100)));
    return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}

/**
 * Walk a Studio document tree and patch the first `Media.Image` node whose id
 * is "logo-image", setting its `props.src` to the provided URL. Mutates the
 * argument in place; returns the same reference for convenience.
 */
function patchLogoNode(node: any, logoUrl: string): any {
    if (!node || typeof node !== 'object') return node;
    if (node.id === 'logo-image' && node.kind === 'Media.Image' && node.props) {
        node.props.src = logoUrl;
    }
    if (node.slots && typeof node.slots === 'object') {
        for (const slotName of Object.keys(node.slots)) {
            const slot = node.slots[slotName];
            if (slot?.items && Array.isArray(slot.items)) {
                for (const child of slot.items) patchLogoNode(child, logoUrl);
            }
        }
    }
    return node;
}

/**
 * Walk a Studio document tree and patch the first node whose id is
 * "store-name" — typically a Heading on the basic-landing starter — to
 * carry the store's actual name. Mirrors `patchLogoNode`; mutates in place
 * and returns the same reference for chaining.
 */
function patchStoreNameNode(node: any, storeName: string): any {
    if (!node || typeof node !== 'object') return node;
    if (node.id === 'store-name' && node.props) {
        // Heading reads props.content; Text reads content too (with text alias).
        // Write content so the patched value lands on both kinds.
        node.props.content = storeName;
    }
    if (node.slots && typeof node.slots === 'object') {
        for (const slotName of Object.keys(node.slots)) {
            const slot = node.slots[slotName];
            if (slot?.items && Array.isArray(slot.items)) {
                for (const child of slot.items) patchStoreNameNode(child, storeName);
            }
        }
    }
    return node;
}

/**
 * Apply the store's branding (primary color + logo URL) to a starter document
 * before sending it to Studio. The bundler resolves tokens to literal hex
 * values at build time, so this is the only chance to influence the deployed
 * page's colors without runtime SDK fetching.
 */
function applyStoreBrandingToDocument(
    doc: any,
    branding: { primaryColor?: string; logoUrl?: string; storeName?: string } | undefined,
): any {
    if (!branding || !doc || typeof doc !== 'object') return doc;
    const cloned = JSON.parse(JSON.stringify(doc));

    if (branding.primaryColor && Array.isArray(cloned.tokens)) {
        const tokenUpdates: Record<string, string> = {
            'color.primary': branding.primaryColor,
            'color.primaryHover': shadeHex(branding.primaryColor, -10),
            'color.accent': branding.primaryColor,
        };
        for (const token of cloned.tokens) {
            if (token?.name && Object.prototype.hasOwnProperty.call(tokenUpdates, token.name)) {
                token.value = tokenUpdates[token.name];
            }
        }
    }

    if (branding.logoUrl && cloned.root) {
        patchLogoNode(cloned.root, branding.logoUrl);
    }

    if (branding.storeName && cloned.root) {
        patchStoreNameNode(cloned.root, branding.storeName);
    }

    return cloned;
}

/**
 * Wrap a starter's `document` into the FunnelTemplate shape that
 * `useTemplateStore.loadTemplate(...)` consumes inside Studio.
 *
 * We construct a single-page FunnelTemplate so Studio loads the starter as the
 * only page and immediately treats it as the canonical page for this funnel
 * step.
 */
function buildFunnelTemplateFromStarter(
    starter: AutoDeployStarter,
    storeBranding?: { primaryColor?: string; logoUrl?: string; storeName?: string },
): unknown {
    const pageType = pageTypeFromCategory(starter.category);
    const pagePath = defaultPagePath(pageType);
    const pageId = generatePageId(pageType);
    const document = applyStoreBrandingToDocument(starter.document, storeBranding);

    return {
        id: starter.starterId,
        name: starter.starterName,
        version: '1.0.0',
        pages: [
            {
                id: pageId,
                name: starter.starterName,
                path: pagePath,
                type: pageType,
                isDefault: true,
                // Omit `features` so Studio's generateManifest falls back to
                // getDefaultFeaturesForPageType — without that, deployed
                // manifests ship with zero `requirements` and the CRM
                // resource-binding panel (offer picker, etc.) renders empty.
                document,
            },
        ],
    };
}

export function StudioAutoDeployFrame({
    builderUrl,
    context,
    starter,
    storeBranding,
    onComplete,
    onError,
    requestSaveDelayMs = 600,
    timeoutMs = 180_000,
}: StudioAutoDeployFrameProps) {
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const hostRef = useRef<BuilderHost | null>(null);
    const settledRef = useRef(false);
    const [iframeSrc] = useState(() => {
        const url = new URL(builderUrl);
        url.searchParams.set('embedded', '1');
        return url.toString();
    });

    useEffect(() => {
        const host = new BuilderHost();
        hostRef.current = host;
        let timeoutHandle: number | undefined;
        let requestSaveHandle: number | undefined;

        const settle = (fn: () => void) => {
            if (settledRef.current) return;
            settledRef.current = true;
            if (timeoutHandle) window.clearTimeout(timeoutHandle);
            if (requestSaveHandle) window.clearTimeout(requestSaveHandle);
            fn();
        };

        const reportError = (message: string) => {
            settle(() => onError(message));
        };

        host.on('ready', () => {
            try {
                host.init({
                    storeId: context.storeId,
                    funnelId: context.funnelId,
                    stepId: context.stepId,
                    stepType: context.stepType as any,
                    sessionToken: context.sessionToken,
                    capabilities: [],
                });

                host.load({
                    mode: 'create',
                    sourceData: buildFunnelTemplateFromStarter(starter, storeBranding),
                    suggestedName: `${starter.starterName} ${context.stepId.slice(-6)}`,
                });

                requestSaveHandle = window.setTimeout(() => {
                    if (settledRef.current) return;
                    host.requestSave();
                }, requestSaveDelayMs);
            } catch (error) {
                reportError(error instanceof Error ? error.message : 'Failed to brief Studio');
            }
        });

        host.on('save', (payload: SaveMessage['payload']) => {
            if (!payload.pluginId || !payload.deploymentId) {
                reportError('Studio save returned without deployment ids');
                return;
            }
            settle(() => onComplete({
                pluginId: payload.pluginId!,
                deploymentId: payload.deploymentId!,
                instanceId: payload.instanceId,
                pagePath: payload.pagePath,
            }));
        });

        timeoutHandle = window.setTimeout(() => {
            reportError('Studio auto-deploy timed out');
        }, timeoutMs);

        const iframe = iframeRef.current;
        if (iframe) {
            host.connect(iframe);
        } else {
            reportError('Auto-deploy iframe ref is null');
        }

        return () => {
            host.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <iframe
            ref={iframeRef}
            src={iframeSrc}
            title="Studio auto-deploy"
            // Off-screen rather than display:none so Studio's render loop runs
            // (some virtualized components skip work when display:none).
            style={{
                position: 'fixed',
                left: '-10000px',
                top: '-10000px',
                width: 1280,
                height: 800,
                opacity: 0,
                pointerEvents: 'none',
                border: 0,
            }}
            aria-hidden="true"
        />
    );
}
